import api from '../api';
import type {
  SoftphoneDndEffective,
  SoftphoneDndRule,
  SoftphoneDndScope,
} from '../../utils/softphoneDnd';
import { clientTimezone } from '../../utils/softphoneDnd';

export interface SoftphoneConfig {
  enabled: boolean;
  camera_id: number;
  ucm_host: string | null;
  ucm_ws_upstream: string | null;
  ws_public_path: string;
  doorbell_sip_extension: string | null;
  ring_timeout_sec: number;
  ami_enabled?: boolean;
  ami_host?: string | null;
  ami_port?: number;
  ami_username?: string | null;
  ami_password_set?: boolean;
  ami_status?: string;
  ami_last_error?: string | null;
}

export interface SoftphoneCredentials {
  extension: string;
  password: string;
  websocket_url: string;
  ucm_host: string | null;
  camera_id: number;
  doorbell_sip_extension: string | null;
  can_use_phone: boolean;
  can_answer_doorbell: boolean;
  ice_servers: Array<{
    urls: string | string[];
    username?: string;
    credential?: string;
  }>;
}

export interface SoftphoneMe {
  user_id: number;
  can_use_phone: boolean;
  can_answer_doorbell: boolean;
  softphone_sip_extension: string | null;
  has_extension: boolean;
  softphone_dnd?: boolean;
  softphone_dnd_scope?: SoftphoneDndScope;
  softphone_dnd_rules?: SoftphoneDndRule[];
  server_timezone?: string;
  dnd_effective?: SoftphoneDndEffective;
  config: {
    enabled: boolean;
    camera_id: number;
    ws_public_path: string;
    doorbell_sip_extension: string | null;
    ring_timeout_sec: number;
  };
}

export interface SoftphoneDndSettings {
  server_timezone: string;
  can_answer_doorbell: boolean;
  manual: { enabled: boolean; scope: SoftphoneDndScope };
  rules: SoftphoneDndRule[];
  effective: SoftphoneDndEffective;
}

export interface SoftphoneContact {
  user_id?: number | null;
  name?: string;
  extension: string;
  kind?: string;
  label?: string;
  username?: string;
}

export interface SoftphoneHistoryItem {
  id: number;
  call_id?: string | null;
  direction: string;
  kind: string;
  peer?: string | null;
  state: string;
  started_at?: string;
  ended_at?: string | null;
  duration_sec?: number | null;
}

export async function fetchSoftphoneConfig(): Promise<SoftphoneConfig> {
  const res = await api.get('/softphone/config');
  return res.data?.data || res.data;
}

export async function putSoftphoneConfig(
  body: Partial<SoftphoneConfig> & { ami_password?: string },
): Promise<SoftphoneConfig> {
  const res = await api.put('/softphone/config', body);
  return res.data?.data || res.data;
}

export async function testSoftphoneConfig(): Promise<{
  success: boolean;
  ucm_reachability: string;
  ucm_host?: string | null;
}> {
  const res = await api.post('/softphone/config/test');
  return res.data;
}

export async function fetchSoftphoneMe(): Promise<SoftphoneMe> {
  const res = await api.get('/softphone/me');
  return res.data?.data || res.data;
}

export async function putSoftphoneDnd(
  enabled: boolean,
  scope?: SoftphoneDndScope,
): Promise<{ softphone_dnd: boolean; softphone_dnd_scope?: SoftphoneDndScope }> {
  const res = await api.put('/softphone/me/dnd', { enabled, scope });
  return res.data?.data || res.data;
}

export async function fetchSoftphoneDndSettings(): Promise<SoftphoneDndSettings> {
  const res = await api.get('/softphone/me/dnd-settings');
  return res.data?.data || res.data;
}

export async function putSoftphoneDndSettings(body: {
  manual_enabled: boolean;
  manual_scope: SoftphoneDndScope;
  rules: Array<{
    id?: string;
    enabled: boolean;
    days: number[];
    start: string;
    end: string;
    scope: SoftphoneDndScope;
    client_timezone?: string;
  }>;
  client_timezone?: string;
}): Promise<SoftphoneDndSettings> {
  const res = await api.put('/softphone/me/dnd-settings', {
    ...body,
    client_timezone: body.client_timezone || clientTimezone(),
  });
  return res.data?.data || res.data;
}

let credsCache: { at: number; data: SoftphoneCredentials } | null = null;
const CREDS_TTL_MS = 5 * 60 * 1000;

export async function fetchSoftphoneCredentials(): Promise<SoftphoneCredentials> {
  if (credsCache && Date.now() - credsCache.at < CREDS_TTL_MS) {
    return credsCache.data;
  }
  const res = await api.post('/softphone/credentials');
  const data = res.data?.data || res.data;
  credsCache = { at: Date.now(), data };
  return data;
}

export function clearSoftphoneCredentialsCache(): void {
  credsCache = null;
}

export async function reportSoftphoneReady(opts?: { claim?: boolean; reclaim?: boolean }): Promise<{
  ok?: boolean;
  transferred?: boolean;
  call_id?: string;
  peer?: string;
  reason?: string;
}> {
  const res = await api.post('/softphone/calls/ready', {
    claim: Boolean(opts?.claim),
    reclaim: Boolean(opts?.reclaim),
  });
  return res.data?.data || res.data || {};
}

export async function reportSoftphoneRinging(payload: {
  call_id?: string;
  from: string;
  kind?: 'doorbell' | 'phone';
}): Promise<any> {
  const res = await api.post('/softphone/calls/ringing', payload);
  return res.data;
}

export async function reportSoftphoneAnswered(callId: string): Promise<any> {
  const res = await api.post(`/softphone/calls/${encodeURIComponent(callId)}/answered`);
  return res.data;
}

export async function reportSoftphoneEnded(
  callId: string,
  body: {
    state?: string;
    peer?: string;
    direction?: string;
    kind?: string;
    duration_sec?: number;
  } = {},
): Promise<any> {
  const res = await api.post(`/softphone/calls/${encodeURIComponent(callId)}/ended`, {
    call_id: callId,
    state: body.state || 'ended',
    peer: body.peer,
    direction: body.direction || 'in',
    kind: body.kind || 'phone',
    duration_sec: body.duration_sec,
  });
  return res.data;
}

export async function fetchSoftphoneHistory(limit = 50): Promise<SoftphoneHistoryItem[]> {
  const res = await api.get('/softphone/calls/history', { params: { limit } });
  const data = res.data?.data || res.data;
  return Array.isArray(data) ? data : (data?.items || []);
}

export async function fetchSoftphoneContacts(): Promise<SoftphoneContact[]> {
  const res = await api.get('/softphone/contacts');
  const data = res.data?.data || res.data;
  const rows = Array.isArray(data) ? data : (data?.items || []);
  return rows.map((r: SoftphoneContact & { name?: string }) => ({
    user_id: r.user_id ?? null,
    name: (r.name || r.label || r.username || '').trim(),
    extension: String(r.extension || ''),
    kind: r.kind,
    label: r.label,
    username: r.username,
  }));
}
